import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import MobiusPage from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import executeContentSearch from "@solveindustrial/Store/Handlers/ContentSearch/ExecuteContentSearch";
import { CustomState } from "@solveindustrial/Store/Reducers";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ContentSearchPageStyles {
    wrapper?: InjectableCss;
}

export const ContentSearchPageStyles: ContentSearchPageStyles = {
    wrapper: {
        css: css`
            margin-bottom: 50px;
        `,
    },
};

const styles = ContentSearchPageStyles;

interface OwnProps extends PageProps {}

const mapStateToProps = (state: CustomState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ query: string }>(location.search);

    return {
        search: location.search,
        searchTerm: parsedQuery.query || "",
        contentFilters: state.contentSearch.contentFilters,
        pagination: state.contentSearch.pagination,
    };
};

const mapDispatchToProps = {
    executeContentSearch,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const ContentSearchPage: React.FC<Props> = ({
    id,
    search,
    searchTerm,
    contentFilters,
    pagination,
    executeContentSearch,
}) => {
    // Use a ref to track previous contentFilters for comparison
    const prevContentFiltersRef = useRef(contentFilters);
    // Add a ref to track whether the component has performed initial search
    const initialSearchPerformedRef = useRef(false);

    useEffect(() => {
        if (!searchTerm) return;

        const prevFilters = prevContentFiltersRef.current;
        const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(contentFilters);
        const searchTermChanged = searchTerm !== contentFilters.query;

        // Always execute search on initial mount
        if (!initialSearchPerformedRef.current || filtersChanged || searchTermChanged) {
            const page = searchTermChanged ? 1 : contentFilters.page || 1;
            const pageSize = contentFilters.pageSize || pagination.defaultPageSize;

            executeContentSearch({ queryString: search, page, pageSize });
            prevContentFiltersRef.current = { ...contentFilters };
            initialSearchPerformedRef.current = true;
        }
    }, [searchTerm, contentFilters, search, executeContentSearch]);

    return (
        <MobiusPage title={searchTerm}>
            <StyledWrapper {...styles.wrapper}>
                {searchTerm && <Typography variant="h1">"{searchTerm}"</Typography>}
                <Zone contentId={id} zoneName="Content"></Zone>
            </StyledWrapper>
            <Modals />
        </MobiusPage>
    );
};

const ConnectedContentSearchPage = connect(mapStateToProps, mapDispatchToProps)(ContentSearchPage);

const pageModule: PageModule = {
    component: ConnectedContentSearchPage,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        fieldDefinitions: [],
        pageType: "System",
    },
};

export default pageModule;
